'use client';

import {
	Close as DialogClose,
	Content as DialogContent,
	Description as DialogDescription,
	Overlay as DialogOverlay,
	Portal as DialogPortal,
	Root as DialogRoot,
	Title as DialogTitle,
} from '@radix-ui/react-dialog';
import { Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCart } from '@/components/cart/cart-provider';
import {
	GEO_COUNTRY_COOKIE_NAME,
	getBrowserCookieValue,
	getMarketByCountry,
	getMarketDisplayFlag,
	getMarketKey,
	type StorefrontMarket,
	setBrowserMarketCookie,
	toCanonicalPath,
} from '@/lib/markets';

interface RegionSuggestionPopupProps {
	market: StorefrontMarket;
	supportedMarkets: StorefrontMarket[];
}

const DISMISS_STORAGE_PREFIX = 'storefront-region-suggestion-dismissed';

export function RegionSuggestionPopup({ market, supportedMarkets }: RegionSuggestionPopupProps) {
	const { updateBuyerIdentity } = useCart();
	const [suggestedMarket, setSuggestedMarket] = useState<StorefrontMarket | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isChanging, setIsChanging] = useState(false);

	useEffect(() => {
		const detectedCountry = getBrowserCookieValue(GEO_COUNTRY_COOKIE_NAME);
		const nextSuggestedMarket = getSuggestedMarket(market, supportedMarkets, detectedCountry);
		setSuggestedMarket(nextSuggestedMarket);

		if (!nextSuggestedMarket || hasDismissedSuggestion(market, nextSuggestedMarket)) {
			setIsOpen(false);
			return;
		}

		setIsOpen(true);
	}, [market, supportedMarkets]);

	if (!suggestedMarket) return null;

	const dismiss = () => {
		storeDismissedSuggestion(market, suggestedMarket);
		setIsOpen(false);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			dismiss();
			return;
		}

		setIsOpen(true);
	};

	const handleSwitchMarket = () => {
		if (isChanging) return;

		setIsChanging(true);
		setBrowserMarketCookie(suggestedMarket);
		void updateBuyerIdentity({ countryCode: suggestedMarket.countryCode });

		const nextPathname = toCanonicalPath(window.location.pathname);
		window.location.assign(`${nextPathname}${window.location.search}${window.location.hash}`);
	};

	return (
		<DialogRoot modal={false} open={isOpen} onOpenChange={handleOpenChange}>
			<DialogPortal>
				<DialogOverlay className="dialog-overlay fixed inset-0 z-[90] bg-black/40 sm:hidden" />
				<DialogContent className="dialog-content fixed inset-x-4 bottom-4 z-[100] max-h-[min(70dvh,24rem)] overflow-hidden rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl outline-none sm:inset-x-auto sm:bottom-8 sm:right-8 sm:w-[22rem] sm:max-w-[calc(100vw-3rem)]">
					<div className="border-b border-neutral-200 px-4 py-3">
						<DialogClose className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-950">
							<span className="sr-only">Close region suggestion</span>
							<X className="h-5 w-5" aria-hidden="true" />
						</DialogClose>

						<DialogTitle className="pr-10 text-sm font-semibold leading-tight text-neutral-950">
							Are you in the right place?
						</DialogTitle>
						<DialogDescription className="mt-1.5 pr-8 text-xs leading-5 text-neutral-500">
							We detected you may be shopping from {suggestedMarket.label}. Switch regions to see
							prices in {suggestedMarket.currencyCode}, or stay in {market.label} with{' '}
							{market.currencyCode} pricing.
						</DialogDescription>
					</div>

					<div className="space-y-1 p-2">
						<button
							type="button"
							disabled={isChanging}
							aria-label={`Switch to ${suggestedMarket.label} with ${suggestedMarket.currencyCode} pricing`}
							className="flex h-10 w-full items-center gap-3 rounded px-3 text-left text-sm font-medium text-neutral-900 transition-colors hover:bg-neutral-100 disabled:cursor-wait disabled:opacity-70"
							onClick={handleSwitchMarket}
						>
							<span className="text-lg leading-none" aria-hidden="true">
								{getMarketDisplayFlag(suggestedMarket)}
							</span>
							<span className="min-w-0 flex-1 truncate">{suggestedMarket.label}</span>
						</button>

						<button
							type="button"
							disabled={isChanging}
							aria-label={`Stay in ${market.label} with ${market.currencyCode} pricing`}
							className="flex h-10 w-full items-center gap-3 rounded bg-neutral-950 px-3 text-left text-sm font-medium text-white transition-colors hover:bg-neutral-900 disabled:cursor-wait disabled:opacity-70"
							onClick={dismiss}
						>
							<span className="text-lg leading-none" aria-hidden="true">
								{getMarketDisplayFlag(market)}
							</span>
							<span className="min-w-0 flex-1 truncate">{market.label}</span>
							<Check className="ml-auto h-4 w-4" aria-hidden="true" />
						</button>
					</div>
				</DialogContent>
			</DialogPortal>
		</DialogRoot>
	);
}

function getSuggestedMarket(
	currentMarket: StorefrontMarket,
	supportedMarkets: StorefrontMarket[],
	detectedCountry: string | undefined,
) {
	const detectedMarket = getMarketByCountry(detectedCountry, supportedMarkets);
	if (!detectedMarket) return null;
	if (getMarketKey(detectedMarket) === getMarketKey(currentMarket)) return null;
	if (detectedMarket.currencyCode.toUpperCase() === currentMarket.currencyCode.toUpperCase()) {
		return null;
	}

	return detectedMarket;
}

function getDismissalStorageKey(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	return `${DISMISS_STORAGE_PREFIX}:${getMarketKey(currentMarket)}:${getMarketKey(suggestedMarket)}`;
}

function hasDismissedSuggestion(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	try {
		return (
			window.localStorage.getItem(getDismissalStorageKey(currentMarket, suggestedMarket)) === '1'
		);
	} catch {
		return false;
	}
}

function storeDismissedSuggestion(
	currentMarket: StorefrontMarket,
	suggestedMarket: StorefrontMarket,
) {
	try {
		window.localStorage.setItem(getDismissalStorageKey(currentMarket, suggestedMarket), '1');
	} catch {
		// Ignore storage failures so private browsing still allows the modal to close.
	}
}
